/**
 * API Client Configuration for Admin Portal
 * Uses the same Gateway Service as the interface project but with ADMIN as X-Portal header
 */

// API Base Configuration
const API_CONFIG = {
  BASE_URL: process.env.NODE_ENV === 'development' 
    ? '' // Use relative URLs in development (proxy will handle routing)
    : process.env.REACT_APP_API_BASE_URL || "https://dev.gotik.vn",
  DEFAULT_PORTAL: process.env.REACT_APP_DEFAULT_PORTAL || "ADMIN",
  TIMEOUT: 30000,
} as const;

console.log('API Configuration:', {
  NODE_ENV: process.env.NODE_ENV,
  REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL,
  BASE_URL: API_CONFIG.BASE_URL,
  DEFAULT_PORTAL: API_CONFIG.DEFAULT_PORTAL
});

// Auth token storage keys (localStorage for admin portal)
const AUTH_STORAGE_KEYS = {
  ACCESS_TOKEN: "admin_access_token",
  REFRESH_TOKEN: "admin_refresh_token",
  ID_TOKEN: "admin_id_token",
  USER_DATA: "admin_user_data",
} as const;

// Types
interface ApiResponse<T = any> {
  data: T;
  status: number;
}

interface GotikApiResponse<T = any> {
  status: {
    code: string;
    message: string;
  };
  data: T;
  metaData: {
    requestId: string;
    timestamp: number;
  };
}

// Token Manager for localStorage
export class TokenManager {
  static getAccessToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  }

  static setAccessToken(token: string): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, token);
  }

  static getRefreshToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  }

  static setRefreshToken(token: string): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, token);
  }

  static getIdToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ID_TOKEN);
  }

  static setIdToken(token: string): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.ID_TOKEN, token);
  }

  static getUserData(): any | null {
    const userData = localStorage.getItem(AUTH_STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  }

  static setUserData(userData: any): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
  }

  static setAuthSession(authTokens: any): void {
    if (authTokens.accessToken) this.setAccessToken(authTokens.accessToken);
    if (authTokens.refreshToken) this.setRefreshToken(authTokens.refreshToken);
    if (authTokens.idToken) this.setIdToken(authTokens.idToken);
  }

  static clearAuthSession(): void {
    Object.values(AUTH_STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  static isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }
}

// HTTP Client with automatic authentication
class ApiClient {
  private baseUrl: string;
  private portal: string;

  constructor(baseUrl: string = API_CONFIG.BASE_URL, portal: string = API_CONFIG.DEFAULT_PORTAL) {
    this.baseUrl = baseUrl;
    this.portal = portal;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<GotikApiResponse<T>>> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const method = options.method || 'GET';
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Portal': this.portal
    };

    // Add ID token header if token exists
    const idToken = TokenManager.getIdToken();
    if (idToken) {
      defaultHeaders['X-Id-Token'] = idToken;
    }

    // Add authorization header if access token exists
    const accessToken = TokenManager.getAccessToken();
    if (accessToken) {
      defaultHeaders['Authorization'] = `Bearer ${accessToken}`;
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      signal: controller.signal,
    };

    try {
      const response = await fetch(url, config);
      clearTimeout(timeoutId);
      
      if (response.status === 401) {
        TokenManager.clearAuthSession();
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server error response:', errorData);
        console.error('Response headers:', Object.fromEntries(response.headers.entries()));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();
      return { data, status: response.status };
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('API request failed:', error);
      
      // Log more details about the error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        console.error('Network error - possible CORS issue or server unavailable');
        console.error('Request URL:', url);
        console.error('Base URL:', this.baseUrl);
      }
      
      throw error;
    }
  }

  // Authentication Methods
  async login(email: string, password: string): Promise<ApiResponse<GotikApiResponse<any>>> {
    return this.request('/auth/sessions', {
      method: 'POST',
      body: JSON.stringify({
        email,
        password,
      }),
    });
  }

  async logout(): Promise<ApiResponse<GotikApiResponse<any>>> {
    const accessToken = TokenManager.getAccessToken();
    const refreshToken = TokenManager.getRefreshToken();

    if (refreshToken) {
      try {
        const result = await this.request('/auth/sessions', {
          method: 'DELETE',
          body: JSON.stringify({
            accessToken,
            refreshToken,
          }),
        });
        TokenManager.clearAuthSession();
        return result;
      } catch (error) {
        console.warn('Server logout failed:', error);
        TokenManager.clearAuthSession();
        throw error;
      }
    }

    TokenManager.clearAuthSession();
    return { data: { data: null } as any, status: 200 };
  }

  async refreshToken(): Promise<ApiResponse<GotikApiResponse<any>>> {
    const refreshToken = TokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    return this.request('/auth/tokens/refresh', {
      method: 'POST',
      body: JSON.stringify({
        refreshToken,
      }),
    });
  }

  async verifyToken(): Promise<ApiResponse<GotikApiResponse<any>>> {
    const accessToken = TokenManager.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    return this.request('/auth/tokens/verification', {
      method: 'GET',
    });
  }

  // Generic HTTP methods
  async get<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<GotikApiResponse<T>>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<ApiResponse<GotikApiResponse<T>>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<ApiResponse<GotikApiResponse<T>>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<GotikApiResponse<T>>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  // Test connectivity method
  async testConnectivity(): Promise<boolean> {
    try {
      console.log('Testing API connectivity...');
      const response = await fetch(`${this.baseUrl}/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Portal': this.portal,
        },
      });
      
      console.log('Connectivity test response:', response.status);
      return response.ok;
    } catch (error) {
      console.error('Connectivity test failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;
